// 介錯の幕2の頭: 真ん中へ歩く代わりに、花嫁の宵渡り (瞬間移動) と同じ見せ方で真ん中へ移り、咆哮の頭を短くする比較版
//   (2026-10-23・開発版 ?execAct2Open=blink だけ。配布版は src/exec_roar_center.ts の歩いて移る版のまま)。
//
//   作者 (10-23・窓口経由・原文): 「方向をもっと短くして、移動も見た感じ、なんかとことこ歩いてて変な感じしたから、
//     瞬間移動で、花嫁の瞬間移動と同じような風に、真ん中に移動してっていう風にすれば良くなるかな？」 (「方向」は咆哮の聞き違い)
//   文書 = docs/research/2026-10-23-exec-quake-dodge-and-size.md の §3
//
//   移り方: 致命の後の静止 (1.1 秒) の後、その場で解けて消え (420ms・花嫁の BLINK_OUT_MS と同じ)、真ん中で結び直る
//     (470ms・BLINK_REAPPEAR_MS と同じ)。絵は花嫁の渦 (blink_swirl) と本体のフェードをそのまま使う (render.ts)。
//     移る先は歩く版と同じ roarCenterTargetX (主人公が真ん中の近くにいれば 136px 空けた所)。消えている間と結び直る間は当たらない。
//   咆哮: 溜めの頭 700ms (待機 → 吠えの構え → 吸気の前半) を削る。波は 350 / 950 / 1510ms (前は 1050 / 1650 / 2210)、
//     突進斬りは 2865ms (前は 3565)。波の間隔・速さ・届く範囲・弾いた時の飛び方・傷と体幹は変えない。
//   切り替えは src/game.ts の DEV の箱の中だけ (setRoarAirFromParams の後に setAct2Blink)。
import { getAttack } from './attacks'
import { FIXED_DT } from './balance'
import { BOSS_ATTACK_TIMELINES, type BossAttackTimeline } from './boss_pose_timeline'
import { ROAR_AIR, ROAR_AIR_TIMELINE, roarAirDir, roarAirOn, roarAirSpawns } from './exec_roar_air'
import { ROAR_CENTER, roarCenterOn, roarCenterTargetX, type RoarCenterTick } from './exec_roar_center'
import type { Boss, HazardSpawn, Player } from './types'

const ATTACK_ID = 'executioner_phaseTransition'
const BOSS_ID = 'the-executioner'

export const ACT2_BLINK = {
  /** 解けて消える (花嫁の BLINK_OUT_MS)。 */
  outMs: 420,
  /** 結び直る (花嫁の BLINK_REAPPEAR_MS)。 */
  inMs: 470,
  /** 結び直ってから吠えるまで。 */
  holdMs: 150,
  /** 咆哮の溜めの頭から削る時間。 */
  cutMs: 700,
} as const

/** 溜めの頭を cutMs 削った絵の時間割。削った所より前のキーは最後の1つ (吠えの構え) だけを 0 に残す。 */
export function cutRoarTimeline(src: BossAttackTimeline, chargeMs: number, cutMs: number): BossAttackTimeline {
  const tele = src.filter((k) => k.phase === 'telegraph')
  const rest = src.filter((k) => k.phase !== 'telegraph')
  const before = tele.filter((k) => k.at * chargeMs < cutMs)
  const after = tele.filter((k) => k.at * chargeMs >= cutMs)
  const nextCharge = chargeMs - cutMs
  const kept = before.length ? [{ ...before[before.length - 1]!, at: 0 }] : []
  const moved = after.map((k) => ({ ...k, at: (k.at * chargeMs - cutMs) / nextCharge }))
  return [...kept, ...moved, ...rest]
}

let on = false
let saved: { chargeMs: number; hazardSpawns: HazardSpawn[] | undefined; timeline: BossAttackTimeline | undefined } | null = null

export function act2BlinkOn(): boolean { return on }
/** 咆哮の描画 (吸気・吠えの光) が読む時刻のずれ。効いている時は削った分を足して、絵と光と波をそろえる。 */
export function act2BlinkRoarCutMs(): number { return on ? ACT2_BLINK.cutMs : 0 }

/** 空気の衝撃波・真ん中へ移る版 (既定) の上でだけ効く。setRoarAir の後に呼ぶ。 */
export function setAct2Blink(next: boolean): void {
  const atk = getAttack(ATTACK_ID)
  const table = BOSS_ATTACK_TIMELINES[BOSS_ID]!
  if (saved) {
    atk.chargeMs = saved.chargeMs
    atk.hazardSpawns = saved.hazardSpawns
    if (saved.timeline) table[ATTACK_ID] = saved.timeline
    saved = null
  }
  on = next && roarAirOn() && roarCenterOn()
  if (!on) return
  saved = { chargeMs: atk.chargeMs, hazardSpawns: atk.hazardSpawns, timeline: table[ATTACK_ID] }
  const cut = ACT2_BLINK.cutMs
  atk.chargeMs = ROAR_AIR.chargeMs - cut
  atk.hazardSpawns = roarAirSpawns(roarAirDir(), ROAR_AIR.centerReachPx)
    .map((s) => ({ ...s, telegraphMs: s.telegraphMs - cut }))
  table[ATTACK_ID] = cutRoarTimeline(ROAR_AIR_TIMELINE, ROAR_AIR.chargeMs, cut)
}

/** 消える・結び直る間の見せ方 (render.ts が花嫁の宵渡りと同じ式で描く)。それ以外は null。 */
export function act2BlinkVisual(boss: Boss): { kind: 'blink' | 'reappear'; elapsedMs: number } | null {
  const rc = boss.roarCenter
  if (!on || !rc) return null
  if (rc.phase === 'blinkOut') return { kind: 'blink', elapsedMs: rc.ms }
  if (rc.phase === 'blinkIn') return { kind: 'reappear', elapsedMs: rc.ms }
  return null
}

/** 消えている・結び直っている間 (当たらない)。 */
export function act2BlinkUntouchable(boss: Boss): boolean {
  const p = boss.roarCenter?.phase
  return p === 'blinkOut' || p === 'blinkIn'
}

/** 1 tick 進める (stepRoarCenter の代わり)。elapsedMs < 0 (致命の後の静止) の間は動かない。 */
export function stepAct2Blink(boss: Boss, player: Player, elapsedMs: number): RoarCenterTick {
  const tickMs = FIXED_DT * 1000
  if (elapsedMs < 0) return { boss, ready: false }
  const state = boss.roarCenter
  if (!state || state.phase === 'move') {
    const target = roarCenterTargetX(boss.x, player.x, boss.id)
    if (Math.abs(target - boss.x) <= ROAR_CENTER.deadbandPx) {
      return { boss: { ...boss, roarCenter: { phase: 'hold', ms: 0 } }, ready: false }
    }
    return { boss: { ...boss, roarCenter: { phase: 'blinkOut', ms: tickMs } }, ready: false }
  }
  const ms = state.ms + tickMs
  if (state.phase === 'blinkOut') {
    if (ms < ACT2_BLINK.outMs) return { boss: { ...boss, roarCenter: { phase: 'blinkOut', ms } }, ready: false }
    const x = roarCenterTargetX(boss.x, player.x, boss.id)
    const facing = player.x >= x ? 'right' as const : 'left' as const
    return { boss: { ...boss, x, facing, roarCenter: { phase: 'blinkIn', ms: 0 } }, ready: false }
  }
  if (state.phase === 'blinkIn') {
    if (ms < ACT2_BLINK.inMs) return { boss: { ...boss, roarCenter: { phase: 'blinkIn', ms } }, ready: false }
    return { boss: { ...boss, roarCenter: { phase: 'hold', ms: 0 } }, ready: false }
  }
  return { boss: { ...boss, roarCenter: { phase: 'hold', ms } }, ready: ms >= ACT2_BLINK.holdMs }
}
